/**
 * @file useHierarchyOperations.js
 * @description Complex hierarchy operations (delete with cascade, zone sections)
 *
 * Extracted from UnifiedBrickSchemaManager.vue Phase 8.4
 * Handles:
 * - Cascade deletion of hierarchy nodes with confirmations
 * - AHU zone section creation with BRICK relationships
 * - Blocking items formatting for user feedback
 */

/**
 * Format blocking items message for display
 * @param {Object} blockingItems - Map of item types to item arrays
 * @returns {string} Formatted message
 */
export const formatBlockingItemsMessage = (blockingItems) => {
  const messages = [];

  for (const [itemType, items] of Object.entries(blockingItems)) {
    if (items && items.length > 0) {
      const itemList = items.slice(0, 3).join(', ');
      const more = items.length > 3 ? ` and ${items.length - 3} more` : '';
      messages.push(`${itemType}: ${itemList}${more}`);
    }
  }

  return messages.join('\n');
};

/**
 * Composable for complex hierarchy operations
 */
export function useHierarchyOperations() {
  /**
   * Delete hierarchy node with cascade support
   *
   * Handles deletion of buildings, levels, spaces, equipment, devices, points, and switchboards
   * with support for cascade deletion and blocking item confirmations.
   *
   * @param {Object} node - The tree node to delete
   * @param {boolean} cascade - Whether to cascade delete children
   * @param {Object} dependencies - Required dependencies
   * @param {Object} dependencies.$q - Quasar instance for notifications
   * @param {Object} dependencies.apis - API composables object
   * @param {Object} dependencies.apis.buildingApi - Building API composable
   * @param {Object} dependencies.apis.equipmentApi - Equipment API composable
   * @param {Object} dependencies.apis.deviceApi - Device API composable
   * @param {Object} dependencies.apis.pointApi - Point API composable
   * @param {Function} dependencies.authenticatedFetch - Authenticated fetch function
   * @param {Function} dependencies.refreshData - Callback to refresh data
   * @param {Function} dependencies.showCascadeDeleteDialog - Callback to show cascade dialog
   * @returns {Promise<void>}
   */
  const deleteHierarchyNode = async (node, cascade = false, dependencies) => {
    const {
      $q,
      apis,
      authenticatedFetch,
      refreshData,
      showCascadeDeleteDialog,
    } = dependencies;

    const { buildingApi, equipmentApi, deviceApi, pointApi } = apis;

    const entityType = node.type;
    const entityId = node.data?.id || node.id;

    if (!entityId) {
      return;
    }

    // Handle building/level/space deletion with composable
    try {
      if (entityType === 'building') {
        await buildingApi.deleteBuilding(entityId, cascade);

        $q.notify({
          type: 'positive',
          message: cascade ? `${node.label} and all related items deleted successfully` : `${node.label} deleted successfully`,
          icon: cascade ? 'check_circle' : 'check',
        });

        await refreshData();
        return;
      }

      if (entityType === 'level') {
        await buildingApi.deleteLevel(entityId, cascade);

        $q.notify({
          type: 'positive',
          message: cascade ? `${node.label} and all related items deleted successfully` : `${node.label} deleted successfully`,
          icon: cascade ? 'check_circle' : 'check',
        });

        await refreshData();
        return;
      }

      if (entityType === 'space') {
        await buildingApi.deleteSpace(entityId, cascade);

        $q.notify({
          type: 'positive',
          message: cascade ? `${node.label} and all related items deleted successfully` : `${node.label} deleted successfully`,
          icon: cascade ? 'check_circle' : 'check',
        });

        await refreshData();
        return;
      }
    } catch (error) {
      // Error handling is done in composable
      return;
    }

    // Handle equipment deletion with composable
    if (entityType === 'zone' || entityType === 'equipment' || entityType === 'zoneSection') {
      try {
        await equipmentApi.deleteEquipment(entityId, cascade);

        $q.notify({
          type: 'positive',
          message: cascade ? `${node.label} and all related items deleted successfully` : `${node.label} deleted successfully`,
          icon: cascade ? 'check_circle' : 'check',
        });

        await refreshData();
        return;
      } catch (error) {
        // Check if this is a cascade-able error from the backend
        if (error.response?.status === 422 && error.response?.data?.can_cascade) {
          showCascadeDeleteDialog(node, error.response.data);
          return;
        }

        // Error notification is shown by composable
        return;
      }
    }

    // For device and point entity types, use composables
    if (entityType === 'device') {
      try {
        await deviceApi.deleteDevice(entityId, cascade);

        $q.notify({
          type: 'positive',
          message: cascade ? `${node.label} and all related items deleted successfully` : `${node.label} deleted successfully`,
          icon: cascade ? 'check_circle' : 'check',
        });

        await refreshData();
        return;
      } catch (error) {
        // Check if this is a cascade-able error from the backend
        if (error.response?.status === 422 && error.response?.data?.can_cascade) {
          showCascadeDeleteDialog(node, error.response.data);
          return;
        }

        // Error notification is shown by composable
        return;
      }
    }

    if (entityType === 'point') {
      try {
        await pointApi.deletePoint(entityId, cascade);

        $q.notify({
          type: 'positive',
          message: cascade ? `${node.label} and all related items deleted successfully` : `${node.label} deleted successfully`,
          icon: cascade ? 'check_circle' : 'check',
        });

        await refreshData();
        return;
      } catch (error) {
        // Check if this is a cascade-able error from the backend
        if (error.response?.status === 422 && error.response?.data?.can_cascade) {
          showCascadeDeleteDialog(node, error.response.data);
          return;
        }

        // Error notification is shown by composable
        return;
      }
    }

    // For other entity types, use the existing endpoint logic
    let endpoint = '';
    switch (entityType) {
      case 'switchboard':
        endpoint = `/api/switchboards/${entityId}`;
        break;
      default:
        $q.notify({
          type: 'negative',
          message: `Cannot delete ${entityType}: Unknown entity type`,
        });
        return;
    }

    // Add cascade parameter if requested
    if (cascade) {
      endpoint += '?cascade=true';
    }

    try {
      const response = await authenticatedFetch(endpoint, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const error = await response.json();

        // Handle specific error cases
        if (response.status === 404) {
          // Item doesn't exist - likely already deleted
          $q.notify({
            type: 'warning',
            message: `${node.label} no longer exists. Refreshing...`,
            icon: 'warning',
          });
          await refreshData(); // Still refresh to update the view
          return;
        }

        // Check if this is a relationship conflict that can be cascaded
        // Backend returns 422 for validation errors including cascade scenarios
        if ((response.status === 409 || response.status === 422) && error.can_cascade) {
          // Show dialog with detailed information about what will be deleted
          showCascadeDeleteDialog(node, error);
          return;
        }

        // Show detailed error message if available
        if (error.blocking_items) {
          const blockingMessage = formatBlockingItemsMessage(error.blocking_items);
          $q.notify({
            type: 'negative',
            message: error.message,
            caption: blockingMessage,
            timeout: 10000,
            actions: [
              { label: 'Dismiss', color: 'white' },
            ],
          });
        } else {
          throw new Error(error.message || `Failed to delete ${entityType}`);
        }
        return;
      }

      // Success message - differentiate between normal and cascade delete
      if (cascade) {
        $q.notify({
          type: 'positive',
          message: `${node.label} and all related items deleted successfully`,
          icon: 'check_circle',
          timeout: 3000,
        });
      } else {
        $q.notify({
          type: 'positive',
          message: `${node.label} deleted successfully`,
          icon: 'check',
        });
      }

      await refreshData();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to delete ${entityType}: ${error.message}`,
        icon: 'error',
      });
    }
  };

  /**
   * Add AHU zone section with equipment and BRICK relationships
   *
   * Creates a zone section under an AHU with proper BRICK relationships:
   * - hasPart (AHU -> Zone)
   * - feeds (Zone -> Space, if specified)
   * - hasLocation (Zone -> Space, inherits from AHU)
   *
   * @param {Object} zoneSectionData - Zone section form data
   * @param {string} zoneSectionData.name - Zone section name
   * @param {string} zoneSectionData.zone_type - Zone type
   * @param {number} [zoneSectionData.template_id] - Optional template ID
   * @param {number} [zoneSectionData.serves_space] - Space ID the zone serves
   * @param {Object} dependencies - Required dependencies
   * @param {Object} dependencies.$q - Quasar instance
   * @param {number} dependencies.siteId - Site ID
   * @param {Object} dependencies.selectedAHU - Selected AHU object with id, name, space_id
   * @param {Object} dependencies.api - API service instance
   * @param {Function} dependencies.resetZoneSectionDialog - Callback to reset dialog
   * @param {Object} dependencies.showZoneSectionDialog - Ref to dialog visibility
   * @param {Function} dependencies.refreshDataWithState - Callback to refresh with state preservation
   * @returns {Promise<void>}
   */
  const addZoneSection = async (zoneSectionData, dependencies) => {
    const {
      $q,
      siteId,
      selectedAHU,
      api,
      resetZoneSectionDialog,
      showZoneSectionDialog,
      refreshDataWithState,
    } = dependencies;

    // Validation
    if (!zoneSectionData.name) {
      $q.notify({
        type: 'negative',
        message: 'Please enter a zone section name',
      });
      return;
    }

    if (!selectedAHU || !selectedAHU.id) {
      $q.notify({
        type: 'negative',
        message: 'No AHU selected for zone section',
      });
      return;
    }

    try {
      // Create the zone section as equipment with parent_equipment_id
      const zoneData = {
        name: zoneSectionData.name,
        brick_class: 'AHU_Zone_Section',
        description: `${zoneSectionData.zone_type} zone section in ${selectedAHU.name}`,
        parent_equipment_id: selectedAHU.id, // Set AHU as parent
        metadata: JSON.stringify({
          zone_type: zoneSectionData.zone_type,
          serves_space_id: zoneSectionData.serves_space,
        }),
      };

      // If a template is selected, include it
      if (zoneSectionData.template_id) {
        zoneData.equipment_template_id = zoneSectionData.template_id;
      }

      // Get the space ID for the AHU to maintain hierarchy
      let spaceId = selectedAHU.space_id;

      if (!spaceId) {
        // Check if space data is nested in the AHU object
        if (selectedAHU.space && selectedAHU.space.id) {
          spaceId = selectedAHU.space.id;
        } else {
          // As a last resort, try to get it from the site hierarchy
          throw new Error('Unable to determine space for creating zone section. The AHU must be associated with a space.');
        }
      }

      // Add space_id to the zone data for v2 API
      zoneData.space_id = spaceId;

      // Create the zone section equipment using v2 API

      let newZone;
      try {
        const response = await api.post(`/v2/sites/${siteId}/equipment`, zoneData);
        newZone = response;
      } catch (apiError) {
        throw new Error(`API call failed: ${apiError.message}`);
      }

      // Validate that we got a valid equipment object back
      if (!newZone || !newZone.id) {
        throw new Error('Failed to create zone section - unexpected response format');
      }


      // Create BRICK relationships
      // 1. Zone hasPart of AHU
      await api.post('/relationships', {
        source_type: 'SiteEquipment',
        source_id: selectedAHU.id,
        target_type: 'SiteEquipment',
        target_id: newZone.id,
        relationship_type: 'hasPart',
      });

      // 2. If zone serves a specific space, create feeds relationship
      if (zoneSectionData.serves_space) {
        await api.post('/relationships', {
          source_type: 'SiteEquipment',
          source_id: newZone.id,
          target_type: 'Space',
          target_id: zoneSectionData.serves_space,
          relationship_type: 'feeds',
        });
      }

      // 3. Zone hasLocation in same space as AHU (if we found the space)
      if (spaceId) {
        await api.post('/relationships', {
          source_type: 'SiteEquipment',
          source_id: newZone.id,
          target_type: 'Space',
          target_id: spaceId,
          relationship_type: 'hasLocation',
        });
      }

      $q.notify({
        type: 'positive',
        message: `Zone section "${zoneSectionData.name}" added to ${selectedAHU.name}`,
        caption: 'You can now add dampers and control points to this zone',
      });

      // Store the IDs we need before resetting the dialog
      const newZoneId = newZone.id;
      const ahuId = selectedAHU.id;

      // Reset and close dialog
      resetZoneSectionDialog();
      showZoneSectionDialog.value = false;

      // Refresh hierarchy while preserving state and expanding the new zone
      const nodesToExpand = [
        `equipment-${ahuId}`,  // Expand the AHU to show the new zone
        `equipment-${newZoneId}`,               // Optionally expand the new zone itself
      ];
      await refreshDataWithState(false, nodesToExpand);

    } catch (error) {

      let errorMessage = 'Failed to add zone section';

      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        if (errors) {
          const errorMessages = [];
          for (const field in errors) {
            errorMessages.push(`${field}: ${errors[field].join(', ')}`);
          }
          errorMessage = 'Validation failed: ' + errorMessages.join('; ');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      $q.notify({
        type: 'negative',
        message: errorMessage,
        timeout: 5000,
      });
    }
  };

  return {
    deleteHierarchyNode,
    addZoneSection,
    formatBlockingItemsMessage,
  };
}
